import { assertPrivateSessionLifetime, capturePrivateSession, type PrivateSessionLifetime } from "@/lib/api/private-session";
import { ApiError, apiFetch } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { uuidv7 } from "@/lib/uuid";
import type { CommandResult } from "@/lib/api/types";

/**
 * THE client half of the invoice command envelope.
 *
 * Centralised on purpose: **no screen may build its own invoice mutation**. One
 * place minting ids and one place retrying is what makes the idempotency
 * guarantee real — a screen that hand-rolls a fetch would silently opt out of it
 * and nobody would notice until a customer was charged twice.
 *
 * The contract with the server:
 *
 * - `id` is a client-generated UUIDv7 and is the PRIMARY KEY of
 *   `restaurant_invoice_commands`. A replay of the same id applies nothing
 *   again and answers with the bill as it stands: exactly the first answer
 *   when the bill has not moved since, and flagged `billChangedSince` when it
 *   has — then the bill is NOT this command's own outcome (`isChangedReplay`).
 * - **A retry reuses the id.** That is the whole point. A new id on retry would
 *   be a second, genuinely different command — which is exactly how a
 *   double-charge happens when the first request actually landed and only its
 *   response was lost.
 * - `applied: false` means "this was a replay". The work happened; it just was
 *   not this call that did it. It is a success, not an error.
 */

export type InvoiceCommandType =
  | "openInvoice"
  | "addItems"
  | "updateDraftItems"
  | "fireItems"
  | "acceptPendingItems"
  | "rejectPendingItems"
  /*
   * The kitchen half of the state machine (D18). These are issued from the
   * kitchen board and from the invoice screen — the SAME transitions, reached
   * by two actuators — which is exactly why they live here with everything
   * else rather than in a second, kitchen-local sender. One place mints ids,
   * one place retries; a board that hand-rolled its own fetch would opt out of
   * the idempotency guarantee, and a double-tapped "start" would write the
   * COOKED depletion twice.
   *
   * `printTicket` is the odd one: its payload is `{ ticketId }` and it carries
   * no `invoiceId`, because the server resolves the invoice from the ticket.
   * The browser chit adds `paperStamp` (the paper it shows) and, as a reprint
   * fallback, the till's `reason` / `seenPrintCount`; the answer's
   * `kitchenPaper.duplicate` says whether it prints «نسخة مكررة» (gate P7
   * rework 2).
   */
  | "printTicket"
  /**
   * D172 — the till's reprint of a ticket that already printed, as a durable
   * job on the station's printer. Carries `{ ticketId, reason }`, reason
   * `NOT_RECEIVED` (the paper never arrived) or `EXTRA_COPY` (only this one
   * prints «نسخة مكررة»). No version bump. A station with no active printer
   * answers 409 `RESTAURANT_STATION_NO_PRINTER`, and the till then prints the
   * browser chit instead. The till sends `liveDeviceOnly: true`: with no LIVE
   * print computer the answer is 409 `RESTAURANT_STATION_PRINTER_OFFLINE`, and
   * the till prints its own chit too (gate P7 rework 2).
   */
  | "reprintTicket"
  | "startPreparing"
  /**
   * The same start, sent from the TILL (T6/D165). Its own type so the server
   * flags the plate as a till start and the speed report leaves its queue and
   * cook stages out — the flag is never a client boolean. Carries `{ itemIds }`.
   */
  | "startPreparingAtTill"
  | "markReady"
  | "markServed"
  /**
   * T3 / D167 — «تراجع» on the last «جاهز» (READY → PREPARING) and on the last
   * «تقديم»/«تسليم» (SERVED → READY, or → PREPARING when it was never marked
   * ready). Carry `{ itemIds }`. Refused with 409 `RESTAURANT_UNDO_UNAVAILABLE`
   * once the plate moved on, past 15 minutes, or after a close's collapse;
   * the bill must be OPEN. Stock-neutral. `undoReady` needs `ITEM_PREPARE`,
   * `undoServed` needs `ITEM_SERVE` — the same rung as the tap it takes back.
   */
  | "undoReady"
  | "undoServed"
  /**
   * Changes the open ingredient swaps on ONE plate the kitchen already holds
   * (B19). Carries `{ itemId, substitutions[] }`.
   *
   * `substitutions` is the plate's set AS IT SHOULD NOW STAND, never a delta —
   * so a replay is a no-op, and an EMPTY array is a real instruction ("take the
   * swaps back off"). Legal while the plate is DRAFT or QUEUED and refused from
   * `PREPARING` onward: once a cook starts, the ingredients are already gone
   * and amending would throw food away with nothing recording it (founder,
   * 2026-09-10). Behind `RESTAURANT_INVOICE_SUBSTITUTE`, the same rung an
   * add-time swap needs.
   */
  | "substituteItem"
  /**
   * Sends ONE plate back to the kitchen to be cooked again. Carries
   * `{ itemId, reasonCode, reasonNote? }`.
   *
   * B117/Q4 made the reason REQUIRED — a fourth closed vocabulary
   * (`REMAKE_REASON_CODES`), refused with a typed 400 if a code from another
   * list arrives. Two conditions came with it: it attributes to a STATION
   * rather than a named person, and the reason rides onto the chit and the
   * kitchen board, so the cook reads an instruction rather than a reproach.
   */
  | "remakeItem"
  | "voidItem"
  | "compItem"
  | "closeInvoice"
  | "settleInvoice"
  | "cancelInvoice"
  /** CLOSED -> OPEN: the undo for a mistaken close (round 4). */
  | "reopenInvoice"
  /** Moves an OPEN invoice to another free table. */
  | "transferInvoice"
  /**
   * Records or corrects how many people are at the table (B6, D45).
   *
   * Covers used to be capturable ONLY at open — and the till opens the bill on
   * the waiter's FIRST MENU TAP, so the one moment the question could be asked
   * was the moment a waiter is least likely to have counted heads. Carries
   * `{ guestCount }`, where `null` is a real answer ("nobody counted after
   * all") so a mis-typed number can be taken back. Legal while the bill is
   * OPEN and refused afterwards: a cover count is a fact about a service, and
   * the per-cover average must not move under a report already read.
   */
  | "setGuestCount"
  /**
   * Corrects WHO an off-premise bill is for (B9, B107) — the name, the phone
   * and the delivery address.
   *
   * Asked once at open and, until this shipped, never again: a mis-typed digit
   * in a rider's phone number had no remedy but cancelling a bill that had
   * already burnt a daily sequence, and only while it was still empty.
   *
   * Carries all three fields every time, each nullable, because `null` is a
   * real answer ("there is no name") and a patch shape would make clearing a
   * field indistinguishable from leaving it alone. Legal while the bill is OPEN
   * or CLOSED and refused once SETTLED — the cheque being printed is exactly
   * when the customer rings back about the flat number, and the food has not
   * arrived yet; once the money is in, editing is a restatement.
   *
   * Refused on a DINE-IN bill as `RESTAURANT_CUSTOMER_NOT_EDITABLE`: a seated
   * bill has no customer, and writing one onto it would make it read as an
   * off-premise order on every screen that branches on the channel.
   *
   * NO FEE AND NO AGGREGATOR ride on it — those are `editCharges` below, on
   * the cashier's rung and OPEN only, because they reprice the bill.
   */
  | "editCustomer"
  /**
   * Corrects the CHANNEL, the delivery FEE and the PLATFORM of an off-premise
   * bill while it is still OPEN (T6, 2026-10-06). Carries
   * `{ channel, deliveryFee, aggregatorId }` — all three every time:
   * `channel` is `TAKEAWAY` or `DELIVERY` (never DINE_IN, in or out),
   * `deliveryFee: null` means "the store's default at this moment" and a
   * number is this bill's own fee, `aggregatorId: null` is direct.
   *
   * The server re-splits the fee for the target channel (a TAKEAWAY zeroes
   * it), re-snapshots the service-charge percent for that channel, snapshots
   * the commission rate off the chosen platform, and nulls the address when
   * the bill leaves DELIVERY. Behind `RESTAURANT_INVOICE_CLOSE` — the rung
   * that may already change what a bill costs — and refused once CLOSED
   * (`RESTAURANT_INVOICE_NOT_OPEN`): the cheque the customer holds must not
   * be repriced under them. Refused on a dine-in bill as
   * `RESTAURANT_CHARGES_NOT_EDITABLE`.
   */
  | "editCharges"
  /**
   * Changes the METHOD of an already-SETTLED bill (D30) — the mis-tap where
   * CASH was recorded on a card sale, which leaves the drawer short by that
   * bill. Carries `{ expectedVersion, settlementMethod }` and NO amount: the
   * server derives that from the target method. Unbounded by business day
   * since S14/D49 (the shift it corrects against is frozen at close); still
   * refused as `RESTAURANT_SETTLEMENT_NOT_CORRECTABLE` on a bill that was
   * never SETTLED.
   */
  | "correctSettlement"
  /**
   * Gives money back off a SETTLED bill (R1, F3) — the only command that takes
   * money OUT of a till.
   *
   * Carries `{ expectedVersion, itemIds[], reasonCode, reasonNote? }` and **no
   * amount and no method**, deliberately: a refund is per PLATE (R2, D15) and
   * every figure — the plate's share of the service charge and of VAT included
   * — is derived server-side from the bill's own stored totals, so there is no
   * code path in which a till could type a refund amount. The method is read
   * off the invoice and is the one the money came in by (R6): cash out of the
   * drawer, card back to the card.
   *
   * Version-guarded like a settle, so a bill that moved under this device loses
   * as a typed conflict. Refused for a BUYER, and behind its own permission
   * rung (`RESTAURANT_INVOICE_REFUND`, R9) which no preset role below OWNER
   * carries — it is granted per person through a custom role.
   */
  | "refundInvoice"
  /**
   * MARKS WHOSE PLATES ARE WHOSE on a shared bill (B43). Carries
   * `{ itemIds[], partyId?, label? }`.
   *
   * THREE EDITS, ONE COMMAND, because all three are the same write to the same
   * column and a waiter correcting a mis-tap should not meet a different verb:
   * a `label` with no `partyId` CREATES the party and marks the plates onto it;
   * a `partyId` marks them onto one that exists; `partyId: null` clears the
   * mark, which is the undo.
   *
   * It moves NO money on its own — it is the labelling that a party settlement
   * then derives an amount from. Legal while the bill is OPEN or CLOSED and
   * refused once SETTLED: re-marking a paid bill would change what a tender
   * already recorded was for.
   */
  | "assignParty"
  /**
   * ONE TENDER against a bill that is not being paid off in a single go (B3).
   *
   * Carries `{ expectedVersion, settlementMethod, settlementAmount?, partyId? }`
   * — and the OPTIONAL amount is the whole difference between the two cases:
   *
   * · A SPLIT TENDER types an amount. The cashier knows the card would only
   *   take 300 of a 500 bill, and the rest is coming in cash.
   * · A PARTY settlement types NOTHING and names a `partyId`. The amount is
   *   derived server-side from that party's plate set, because a till that
   *   could type what a family owes could type the wrong number.
   *
   * The bill closes itself when the outstanding balance reaches zero; until it
   * does, each tender is its own ledger row and the drawer holds the cash the
   * moment it is taken. Every tender is version-guarded, so two cashiers on two
   * devices cannot both take "the rest".
   */
  | "settlePartial"
  /**
   * HANDS BACK MONEY THE BILL NO LONGER ASKS FOR (B89). Carries
   * `{ expectedVersion, reasonCode, reasonNote? }` and NO AMOUNT.
   *
   * The act that finishes a bill which was part-paid and THEN shortened: 300
   * taken of 500, a plate the family never got voided, re-closed at 100. The
   * figure is `tendered − total`, both the server's own — this is the only
   * command in the product that pays out without a plate to price it from, so
   * a till that could type it could type the wrong number.
   *
   * On `RESTAURANT_INVOICE_REFUND` (R9), which no preset role carries: money
   * physically leaving the till is the refund rung's subject whatever prompted
   * it, and the founder chose that over the void rung on 2026-09-12 precisely
   * so this does not become a cash-out path for everyone who may take a line
   * off a bill.
   */
  | "refundOverpayment"
  /**
   * RECORDS A PRE-BILL CHECK on an OPEN bill (round 5). Carries
   * `{ splitGuests? }` — 2 to 5, or absent for no split — and NO figures: the
   * server prices the check itself and stamps the bill's last check (time,
   * total, who, count).
   *
   * The table stays open: no status moves, no money, no cheque, and the
   * `version` does not change, so it never makes a close conflict. Behind
   * `RESTAURANT_INVOICE_APPEND` — floor work, the waiter at the table. The
   * paper is printed AFTER this succeeds, through the browser print path
   * (`docs/restaurant-printing.md`), and a printer that fails cannot undo it.
   */
  | "printCheck"
  /**
   * D120 — A WAITER'S "SEND TO CASHIER". Carries no payload. Stamps the OPEN
   * bill as waiting for someone who may send to the kitchen; the plates stay
   * DRAFT, and the next fire clears the stamp. Behind `INVOICE_APPEND`. A bill
   * with nothing unsent is a no-op.
   */
  | "requestCashier"
  /**
   * D153 — NAMES «الكابتن» on an OPEN dine-in bill, or clears it. Carries
   * `{ captainSellerId: string | null }` — required, `null` is "no captain".
   * Behind `INVOICE_CLOSE` (cashier, manager, owner). Refused off DINE_IN
   * (`RESTAURANT_CAPTAIN_NOT_DINE_IN`), once the bill is not OPEN, and for a
   * member who is not this store's waiter (`RESTAURANT_CAPTAIN_NOT_ELIGIBLE`).
   * Each change is its own row in the command log.
   */
  | "setCaptain"
  /**
   * D146 — A DISCOUNT AT THE BILL («الخصم»). Carries `{ kind, value,
   * reasonCode, reasonNote? }`: a PERCENT, a NET AMOUNT, or a TARGET_TOTAL
   * («خليها 230») the SERVER back-solves. OPEN bills only, one per bill (an
   * apply replaces), off the net food before service and VAT. Behind
   * `RESTAURANT_INVOICE_VOID`.
   */
  | "applyBillDiscount"
  /** D146 — takes the bill's discount off. No payload. */
  | "removeBillDiscount";

export type InvoiceCommand = {
  type: InvoiceCommandType;
  invoiceId?: string;
  payload: Record<string, unknown>;
};

/**
 * D156 — a manager's own password over ONE command. Sent TOP-LEVEL beside
 * `payload` (the payload is what the server's command log stores), and never
 * part of a `PreparedCommand`: it is handed to `sendCommand` per send, so no
 * retained envelope, recovery map or cache ever holds it.
 */
export type CommandApproval = {
  approverSellerId: string;
  password: string;
};

/** D156 — the four refusals the approval sheet answers. */
export const APPROVAL_REQUIRED_CODE = "RESTAURANT_APPROVAL_REQUIRED";
export const APPROVAL_INVALID_CODE = "RESTAURANT_APPROVAL_INVALID";
export const APPROVAL_LOCKED_CODE = "RESTAURANT_APPROVAL_LOCKED";
/**
 * The approver's RIGHT password is still their one-time temporary one: they
 * must replace it by signing in first. Only ever sent after the password
 * matched, and never counted toward the lockout.
 */
export const APPROVAL_PASSWORD_CHANGE_REQUIRED_CODE =
  "RESTAURANT_APPROVAL_PASSWORD_CHANGE_REQUIRED";

export function isApprovalRefusal(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    (error.code === APPROVAL_REQUIRED_CODE ||
      error.code === APPROVAL_INVALID_CODE ||
      error.code === APPROVAL_LOCKED_CODE ||
      error.code === APPROVAL_PASSWORD_CHANGE_REQUIRED_CODE)
  );
}

/**
 * A command with its id already minted.
 *
 * Held by the caller across retries. Building this once and sending it many
 * times is the supported shape; calling `sendCommand` twice for one user action
 * is not.
 */
export type PreparedCommand = InvoiceCommand & { id: string };

const commandLifetimes = new WeakMap<PreparedCommand, PrivateSessionLifetime>();
export function prepareCommand(command: InvoiceCommand): PreparedCommand {
  const prepared = { ...command, id: uuidv7() };
  commandLifetimes.set(prepared, capturePrivateSession());
  return prepared;
}

/** Network failures worth retrying automatically. */
function isTransient(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return false;
  if (error instanceof ApiError) {
    // D156: a locked approver is a 429 that a retry seconds later cannot fix —
    // the lock lasts until the oldest refusal ages out of fifteen minutes.
    if (error.code === APPROVAL_LOCKED_CODE) return false;
    // 5xx and 429 may succeed on a second attempt; 4xx will not, and retrying a
    // typed conflict would just burn the user's time before showing them the
    // recovery they need.
    return error.status >= 500 || error.status === 429;
  }
  // A thrown TypeError from fetch is the dropped-connection case — precisely
  // the one where the request may well have landed, so reusing the id matters.
  return true;
}

const RETRY_DELAYS_MS = [400, 1200];

export async function sendCommand(
  command: PreparedCommand,
  options?: {
    headers?: Record<string, string>;
    signal?: AbortSignal;
    /** D156 — for THIS send only; see `CommandApproval`. */
    approval?: CommandApproval;
  },
): Promise<CommandResult> {
  const lifetime = commandLifetimes.get(command) ?? capturePrivateSession();
  commandLifetimes.set(command, lifetime);
  assertPrivateSessionLifetime(lifetime);
  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      if (options?.signal?.aborted) throw new DOMException("Command request aborted", "AbortError");
      assertPrivateSessionLifetime(lifetime);
      return await apiFetch<CommandResult>(endpoints.restaurant.commands, {
        method: "POST",
        headers: options?.headers,
        signal: options?.signal,
        body: {
          // Same id on every attempt.
          id: command.id,
          type: command.type,
          invoiceId: command.invoiceId,
          payload: command.payload,
          clientTimestamp: new Date().toISOString(),
          ...(options?.approval ? { approval: options.approval } : {}),
        },
      });
    } catch (error) {
      lastError = error;
      if (!isTransient(error) || attempt === RETRY_DELAYS_MS.length) {
        throw error;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_DELAYS_MS[attempt]),
      );
    }
  }

  throw lastError;
}

/**
 * In-memory recovery for this mounted invoice screen (not an offline queue).
 * A failed response cannot tell us whether a mutation committed. Retain its
 * exact envelope until a replay succeeds; the next completed action mints a new id.
 */
export function createRecoverableCommandSender() {
  const lifetime = capturePrivateSession();
  const uncertain = new Map<string, PreparedCommand>();
  return async (
    command: InvoiceCommand,
    options?: { approval?: CommandApproval },
  ): Promise<CommandResult & { commandId: string }> => {
    assertPrivateSessionLifetime(lifetime);
    // The fingerprint and the retained envelope are the COMMAND only: a
    // manager's approval (D156) rides on this one send and is never kept, so a
    // retry of an uncertain command reuses its id with whatever approval the
    // sheet collects then — and a replay needs none.
    const fingerprint = JSON.stringify(command);
    const prepared = uncertain.get(fingerprint) ?? prepareCommand(command);
    uncertain.set(fingerprint, prepared);
    try {
      const result = await sendCommand(prepared, {
        approval: options?.approval,
      });
      uncertain.delete(fingerprint);
      return { ...result, commandId: prepared.id };
    } catch (failure) {
      // Typed business refusals have not applied. An old uncertain server row
      // remains uncertain, so it must not turn into a new mutation on retry.
      if (
        !isTransient(failure) &&
        !(
          failure instanceof ApiError &&
          failure.code === "RESTAURANT_COMMAND_OUTCOME_UNCERTAIN"
        )
      ) {
        uncertain.delete(fingerprint);
      }
      throw failure;
    }
  };
}

/** Convenience for the common one-shot case. */
export function dispatchCommand(
  command: InvoiceCommand,
): Promise<CommandResult> {
  return sendCommand(prepareCommand(command));
}

/**
 * P4.1 — a replay whose bill moved since its first answer. The command DID
 * apply (once, earlier); the bill it carries is the bill as it stands, so a
 * screen must refresh and look rather than chain or compute from it — the
 * same recovery as a stale-bill conflict, for a command that went through.
 */
export function isChangedReplay(result: CommandResult): boolean {
  return result.applied === false && result.billChangedSince === true;
}

/** The typed conflict decision D9 produces on a stale close or settle. */
export const VERSION_CONFLICT_CODE = "RESTAURANT_INVOICE_VERSION_CONFLICT";

export function isVersionConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === VERSION_CONFLICT_CODE;
}

/**
 * D22's other stale-close refusal: the close gate runs BEFORE the version
 * guard, so an invoice that moved under this device comes back as
 * `RESTAURANT_INVOICE_HAS_PENDING_ITEMS` exactly where it used to come back as
 * a version conflict — a buyer request arrived, or another till fired a plate,
 * between this device's last refresh and its tap.
 */
export const HAS_PENDING_ITEMS_CODE = "RESTAURANT_INVOICE_HAS_PENDING_ITEMS";

export function hasPendingItemsConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === HAS_PENDING_ITEMS_CODE;
}

/**
 * P9 (backlog Part P, P9b) — a command id past the 7-day replay window
 * (409, `retentionDays: 7`). Nothing ran now and the id must not be sent
 * again. On an invoice command it means the command DID apply, more than 7
 * days ago, and its answer is no longer kept — so the till answers it as it
 * answers a changed replay: the «تم تنفيذ هذا الطلب من قبل…» card and a
 * refetch. Unreachable in normal use: this screen keeps an uncertain id only
 * while it is mounted.
 */
export const COMMAND_EXPIRED_CODE = "RESTAURANT_COMMAND_EXPIRED";

export function isExpiredCommand(error: unknown): boolean {
  return error instanceof ApiError && error.code === COMMAND_EXPIRED_CODE;
}

/**
 * The refusals a refresh-and-look recovery answers (D22, and P9's expired
 * replay).
 *
 * All mean the same thing to a cashier — "this invoice is not what your screen
 * says it is" — and all are fixed the same way: refetch, read what changed,
 * act again. Anything else is a toast; these are the inline recovery card.
 */
export function isRecoverableConflict(error: unknown): boolean {
  return (
    isVersionConflict(error) ||
    hasPendingItemsConflict(error) ||
    isExpiredCommand(error)
  );
}

export function isPermissionDenied(error: unknown): boolean {
  return error instanceof ApiError && error.code === "SELLER_PERMISSION_DENIED";
}
